// AllUsers.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import RetailerProfileModal from '../../components/retailer-profile-modal';
import { FaSearch, FaTimes, FaRupeeSign, FaWallet, FaArrowUp, FaArrowDown, FaGift, FaMoneyCheck, FaSms, FaServer, FaCheck, FaCopy } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';

function AllUsers() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const navigate = useNavigate();

    // Modal states
    const [retailerModalOpen, setRetailerModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [balanceModalOpen, setBalanceModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Balance Form States
    const [balanceForm, setBalanceForm] = useState({
        serviceType: '',
        amount: '',
        transactionId: '',
        remark: '',
        selectedColumns: {
            serviceType: false,
            amount: false,
            transactionId: false
        }
    });

    // Service Type Options
    const serviceTypes = [
        { id: 'manual_add', label: 'Manual Add', icon: FaArrowUp, color: 'text-green-600' },
        { id: 'manual_debit', label: 'Manual Debit', icon: FaArrowDown, color: 'text-red-600' },
        { id: 'bonus', label: 'Bonus', icon: FaGift, color: 'text-yellow-600' },
        { id: 'payout', label: 'Payout', icon: FaMoneyCheck, color: 'text-blue-600' },
        { id: 'sms_charges', label: 'SMS Charges', icon: FaSms, color: 'text-purple-600' },
        { id: 'server_fees', label: 'Server Fees', icon: FaServer, color: 'text-indigo-600' }
    ];

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchValue: '',
        status: '',
        role: '',
        dateFrom: '',
        dateTo: ''
    });

    // Role options
    const roleOptions = [
        { id: '', name: 'All Roles' },
        { id: '1', name: 'Admin' },
        { id: '2', name: 'Manager' },
        { id: '3', name: 'Retailer' },
        { id: '4', name: 'Agent' },
        { id: '5', name: 'Sub-Agent' },
        { id: '6', name: 'User' },
        { id: '7', name: 'Distributor' }
    ];

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '2', name: 'Inactive' },
        { id: '3', name: 'Suspended' },
        { id: '4', name: 'Pending' },
        { id: '5', name: 'Blocked' },
        { id: '6', name: 'Deleted' }
    ];

    // Fetch Users with pagination
    const fetchUsers = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                role: filters.role,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall("all-users", params, true);

            if (result.status === true) {
                setUsers(result.data || []);
                setFilteredUsers(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch users");
                setUsers([]);
                setFilteredUsers([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle User Click - Open Retailer Profile Modal
    const handleUserClick = (userId) => {
        setSelectedUserId(userId);
        setRetailerModalOpen(true);
    };

    // Handle Balance Click
    const handleBalanceClick = (user) => {
        setSelectedUser(user);
        setBalanceForm({
            serviceType: '',
            amount: '',
            transactionId: generateTransactionId(),
            remark: '',
            selectedColumns: {
                serviceType: false,
                amount: false,
                transactionId: false
            }
        });
        setBalanceModalOpen(true);
    };

    // Generate Transaction ID
    const generateTransactionId = () => {
        const prefix = 'TXN';
        const timestamp = Date.now().toString().slice(-8);
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `${prefix}${timestamp}${random}`;
    };

    // Convert number to words - Fixed version with decimal handling
    const numberToWords = (num) => {
        if (!num || isNaN(num)) return '';

        // Handle decimal numbers by splitting
        const numStr = num.toString();
        const parts = numStr.split('.');
        const integerPart = parseInt(parts[0]);
        const decimalPart = parts[1] ? parseInt(parts[1]) : 0;

        if (integerPart === 0 && decimalPart === 0) return 'Zero';

        let result = '';

        // Convert integer part
        if (integerPart > 0) {
            result += convertIntegerToWords(integerPart);
        }

        // Convert decimal part
        if (decimalPart > 0) {
            if (result) result += ' Point ';
            result += convertIntegerToWords(decimalPart);
        }

        return result;
    };

    // Helper function to convert integer to words
    const convertIntegerToWords = (num) => {
        if (num === 0) return '';

        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        if (num < 10) {
            return ones[num];
        } else if (num < 20) {
            return teens[num - 10];
        } else if (num < 100) {
            const ten = Math.floor(num / 10);
            const one = num % 10;
            return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
        } else if (num < 1000) {
            const hundred = Math.floor(num / 100);
            const remainder = num % 100;
            return ones[hundred] + ' Hundred' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        } else if (num < 100000) {
            const thousand = Math.floor(num / 1000);
            const remainder = num % 1000;
            return convertIntegerToWords(thousand) + ' Thousand' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        } else if (num < 10000000) {
            const lakh = Math.floor(num / 100000);
            const remainder = num % 100000;
            return convertIntegerToWords(lakh) + ' Lakh' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        }

        return num.toString();
    };

    // Handle form change
    const handleBalanceFormChange = (key, value) => {
        setBalanceForm(prev => ({ ...prev, [key]: value }));
    };

    // Handle column toggle
    const toggleColumn = (column) => {
        setBalanceForm(prev => ({
            ...prev,
            selectedColumns: {
                ...prev.selectedColumns,
                [column]: !prev.selectedColumns[column]
            }
        }));
    };

    // Submit balance transaction
    const handleSubmitBalance = async () => {
        // Check if all required confirmations are checked
        const allConfirmed = Object.values(balanceForm.selectedColumns).every(value => value === true);

        if (!allConfirmed) {
            toast.error('Please confirm all checkboxes before submitting');
            return;
        }

        if (!balanceForm.serviceType) {
            toast.error('Please select a service type');
            return;
        }
        if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        try {
            const params = {
                userId: selectedUser.user_id,
                serviceType: balanceForm.serviceType,
                amount: balanceForm.amount,
                transactionId: balanceForm.transactionId,
                remark: balanceForm.remark
            };

            const result = await apiCall("manage-balance", params, true);

            if (result.status === true) {
                toast.success('Balance updated successfully!');
                setBalanceModalOpen(false);
                fetchUsers(pagination.currentPage);
            } else {
                toast.error(result.message || 'Failed to update balance');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchUsers(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchUsers(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: '',
            role: '',
            dateFrom: '',
            dateTo: ''
        });
        fetchUsers(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchUsers(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-yellow-100 text-yellow-800',
            'Suspended': 'bg-red-100 text-red-800',
            'Pending': 'bg-blue-100 text-blue-800',
            'Blocked': 'bg-gray-100 text-gray-800',
            'Deleted': 'bg-purple-100 text-purple-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get role color
    const getRoleColor = (role) => {
        const colors = {
            'Admin': 'bg-purple-100 text-purple-800',
            'Manager': 'bg-blue-100 text-blue-800',
            'Retailer': 'bg-green-100 text-green-800',
            'Agent': 'bg-yellow-100 text-yellow-800',
            'Sub-Agent': 'bg-orange-100 text-orange-800',
            'User': 'bg-gray-100 text-gray-800',
            'Distributor': 'bg-indigo-100 text-indigo-800'
        };
        return colors[role] || 'bg-gray-100 text-gray-800';
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">All Users</h1>
                        <p className="text-gray-600 text-sm">View and manage all registered users</p>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                {/* Column 1: Search */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Search
                                        </label>
                                        <div className="relative">
                                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={filters.searchValue}
                                                onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                                placeholder="Search by name, email, mobile..."
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Column 2: Date Range */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date Range
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="date"
                                                value={filters.dateFrom}
                                                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                            <input
                                                type="date"
                                                value={filters.dateTo}
                                                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Column 3: Status + Role */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status.id} value={status.id}>
                                                {status.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Role
                                    </label>
                                    <select
                                        value={filters.role}
                                        onChange={(e) => handleFilterChange('role', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {roleOptions.map(role => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 4: Actions */}
                                <div className="space-y-2 flex flex-col justify-end">
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={applyFilters}
                                            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                        >
                                            Apply
                                        </button>
                                        <button
                                            onClick={resetFilters}
                                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredUsers.length}</span> users
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Users Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mobile
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Balance
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Registered
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plan
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => (
                                            <tr
                                                key={user.user_id}
                                                className="hover:bg-gray-50 transition-colors duration-150"
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => handleUserClick(user.user_id)}>
                                                    {user.user_id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => handleUserClick(user.user_id)}>
                                                    {user.name}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {user.email}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 cursor-pointer underline" onClick={() => handleUserClick(user.user_id)}>
                                                    {user.mobile}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 cursor-pointer" onClick={() => handleBalanceClick(user)}>
                                                    ₹{parseFloat(user.balance || 0).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${getRoleColor(user.role)}`}>
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${getStatusColor(user.status)}`}>
                                                        {user.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {user.date || 'N/A'}
                                                    <br />
                                                    <span className="text-xs text-gray-400">
                                                        {user.time || ''}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 underline cursor-pointer" onClick={() =>
                                                    navigate(`/margin/setup`, {
                                                        state: { plan_id: user.plan_id },
                                                    })
                                                }>
                                                    {user.plan_name || 'Default'}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No users found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                {/* Page numbers */}
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <RetailerProfileModal
                isOpen={retailerModalOpen}
                onClose={() => setRetailerModalOpen(false)}
                userId={selectedUserId}
            />

            {/* Balance Management Modal - Perfectly Centered with Fixed Header & Footer */}
            {balanceModalOpen && selectedUser && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setBalanceModalOpen(false);
                        }
                    }}
                >
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                        {/* Fixed Header */}
                        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{selectedUser.name} (ID: {selectedUser.user_id})</h2>
                            </div>
                            <button
                                onClick={() => setBalanceModalOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FaTimes className="text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {/* Service Type */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Choose Service Type <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {serviceTypes.map((service) => {
                                        const Icon = service.icon;
                                        const isSelected = balanceForm.serviceType === service.id;
                                        return (
                                            <button
                                                key={service.id}
                                                onClick={() => handleBalanceFormChange('serviceType', service.id)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${isSelected
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <Icon className={service.color} />
                                                <span className="text-xs sm:text-sm">{service.label}</span>
                                                {isSelected && <FaCheck className="ml-auto text-blue-500 text-xs" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Amount */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Amount <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={balanceForm.amount}
                                        onChange={(e) => handleBalanceFormChange('amount', e.target.value)}
                                        placeholder="Enter amount"
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                {balanceForm.amount && parseFloat(balanceForm.amount) > 0 && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {numberToWords(parseFloat(balanceForm.amount))}
                                    </p>
                                )}
                            </div>

                            {/* Transaction ID */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Transaction ID <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    <input
                                        type="text"
                                        value={balanceForm.transactionId}
                                        onChange={(e) => handleBalanceFormChange('transactionId', e.target.value)}
                                        className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm"
                                    />
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(balanceForm.transactionId);
                                            toast.success('Transaction ID copied!');
                                        }}
                                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                        title="Copy Transaction ID"
                                    >
                                        <FaCopy className="text-gray-600" />
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-blue-600 cursor-pointer hover:underline"
                                    onClick={() => handleBalanceFormChange('transactionId', generateTransactionId())}>
                                    Click to auto-generate new transaction ID
                                </p>
                            </div>

                            {/* Remark */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Remark
                                </label>
                                <textarea
                                    value={balanceForm.remark}
                                    onChange={(e) => handleBalanceFormChange('remark', e.target.value)}
                                    placeholder="Add a remark (optional)"
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Column Selection - All unchecked by default */}
                            <div className="border-t border-gray-200 pt-4">
                                <p className="text-sm font-medium text-gray-700 mb-3">Please confirm the following:</p>
                                <div className="flex flex-wrap gap-4">
                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={balanceForm.selectedColumns.serviceType}
                                            onChange={() => toggleColumn('serviceType')}
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span>Service Type</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={balanceForm.selectedColumns.amount}
                                            onChange={() => toggleColumn('amount')}
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span>Amount</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={balanceForm.selectedColumns.transactionId}
                                            onChange={() => toggleColumn('transactionId')}
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span>Transaction ID</span>
                                    </label>
                                </div>
                                {!Object.values(balanceForm.selectedColumns).every(v => v === true) && (
                                    <p className="mt-2 text-xs text-red-500">
                                        Please check all confirmations before submitting
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Fixed Footer */}
                        <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-xl flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleSubmitBalance}
                                className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                            >
                                Submit Transaction
                            </button>
                            <button
                                onClick={() => setBalanceModalOpen(false)}
                                className="flex-1 px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default AllUsers;